// Pure helpers extracted from api/client.ts — query string building + error parsing.

/**
 * Build a URL query string from optional params.
 * Omits keys whose value is undefined/null.
 * Returns empty string when no params survive, or "?key=val&..." otherwise.
 *
 * ⚠️ NO PRODUCTION CALLER SINCE R-68(2). The last one was the client's `GET /v1/admin/users`
 * read, which left with the caller-less client functions. Two gates still name this symbol:
 * `api/__tests__/core-api-route-parity.test.ts` and `api/__tests__/body-parity-scan.ts` both
 * match `^buildQueryString\(` in `QUERY_TAIL_EXPRESSIONS` to classify a query tail on a client
 * call, so the next client function that takes query params has a classifier waiting for it.
 * `__tests__/api-utils.test.ts` keeps it covered.
 */
export function buildQueryString(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

/**
 * Extract a human-readable error message from a JSON response body.
 * Falls back to the provided default if the body is not an object with a `message` field.
 */
export function parseErrorMessage(body: unknown, fallback: string): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof (body as Record<string, unknown>).message === 'string'
  ) {
    return (body as Record<string, unknown>).message as string;
  }
  return fallback;
}

/**
 * Extract the RFC 6749 `error` code from an error-response body, if present.
 * Returns null for non-object bodies or a missing/non-string `error` member.
 */
export function extractOAuthErrorCode(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const error = (body as { error?: unknown }).error;
  if (typeof error !== 'string') return null;
  return error;
}
